import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Upload,
  Icon,
  Row,
  Col,
  message,
  Popover,
  Tooltip,
} from "antd";
import { renderNotification } from "../../../components/orders/OrderTypeIcons";
import { getOsdReasons, fetchOsdDetails, SaveOsdDetails, DeleteOsdDetails } from "../../../api/OrdersApi";
import { randomUUID } from "../../../common/Common";
import { setTextRenderingMode } from "pdf-lib";

const { Option } = Select;
const { TextArea } = Input

const OSD_STATUS_OPTIONS = [
  { value: "OPEN", label: "OPEN" },
  { value: "REVIEW", label: "REVIEW" },
  { value: "CLAIM", label: "CLAIM" },
  { value: "CLOSED", label: "CLOSED" },
];

const createEmptyRow = () => ({
  osd_reason: undefined,
  notes: "",
  qty: null,
  units: null,
  fileList: [],
  status: undefined,
});

const MAX_NOTE_LENGTH = 55;

const normalizeRows = (value) => {
  if (Array.isArray(value) && value.length > 0) {
    return value.map((row) => ({
      ...createEmptyRow(),
      ...row,
      fileList: Array.isArray(row?.fileList) ? row.fileList : [],
    }));
  }

  return [createEmptyRow()];
};

// Get current time in IST (UTC+5:30)
const getISTTimestamp = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST = UTC + 5:30
  const istTime = new Date(now.getTime() + istOffset);
  return istTime.toISOString().replace("Z", "+05:30");
};

function Modal_OSD({ visible, onCancel, onSave, orderId,locationId, refreshDetails }) {
  const [rows, setRows] = useState(() => [createEmptyRow()]);

  const [errorMsz, setErrorMsz] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [focusedNoteIndex, setFocusedNoteIndex] = useState(null);

  const [trackApicall, setTrackApiCall] = useState(false);

  const fetchReasons = async () => {
    setTrackApiCall(false)
    try {
      const response = await getOsdReasons(orderId);
      setReasons(response.data);
      setUsers(response.users);
      setItems(response.items)
    } catch (error) {
      console.error("Error fetching OSD reasons:", error);
    }
  };

  const getOsdDetails = async (orderId) => {
    try {
      const response = await fetchOsdDetails(orderId , locationId);
      setRows(normalizeRows(response?.data));
    } catch (error) {
      console.error("Error fetching OSD details:", error);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchReasons();
      setErrorMsz("");
      if (orderId) {
        getOsdDetails(orderId);
      }
    }
  }, [visible]);

  useEffect(() => {
    if (orderId) {
      setTrackApiCall(false);
      getOsdDetails(orderId);
    }
  }, [orderId]);

const handleChange = (index, field, value) => {
  setRows((prevRows) =>
    prevRows.map((row, i) =>
      i === index
        ? { ...row, [field]: value }
        : row
    )
  );
};

  const addRow = () => {
    setRows((prevRows) => [...normalizeRows(prevRows), createEmptyRow()]);
  };

  const removeRow = async (index) => {
    const rowToDelete = rows[index];
    if(rowToDelete?.id) {   
        await DeleteOsdDetails(orderId,rowToDelete.id,locationId).then((res) => {

          if(res?.success) {
            setTrackApiCall(true);
          }
          if(rows.length === 1) {
            refreshDetails();
            onCancel(false);
          }
          if (res?.success === false) {
            const errorMessage = res?.errors?.[0] || "Failed to delete OSD details.";
            renderNotification(errorMessage); 
            return;
          } 
        })
        message.success(" deleted successfully");
    }

      setRows((prevRows) => {
        const updatedRows = normalizeRows(prevRows).filter(
          (_, i) => i !== index
        );

        return updatedRows.length ? updatedRows : [createEmptyRow()];
      });

      // await getOsdDetails(orderId);
  };

const handleUploadChange = (index, info) => {
  setRows((prevRows) => {
    const updatedRows = normalizeRows(prevRows);

    updatedRows[index] = {
      ...updatedRows[index],
      fileList: info.fileList.slice(-1), // only keep latest file
      file_name: info.fileList?.[0]?.name || null,
      file_type: info.fileList?.[0]?.type || null,
    };

    return updatedRows;
  });
};

  const handleSave = async () => {
   const errors = [];

    const hasOSDError = rows.some(
      row => !row.osd_reason
    );

    const hasAuthError = rows.some(
      row => row.status === "CLOSED" && !row.authorized_by
    );

    if (hasOSDError) {
      errors.push("OSD Reason cannot be blank");
    }

    if (hasAuthError) {
      errors.push("Reported By cannot be blank");
    }

    if (errors.length > 0) {
      setErrorMsz(errors);
      renderNotification(errors.join(", "));
      return;
    }

    setErrorMsz([]);
setLoading(true);
    try {
      // Build payload with binary file and IST timestamp
      const payload = normalizeRows(rows).map((row) => {
        const selectedFile = row.fileList?.[0] || null;
        const fileName = selectedFile?.name || row.file_name || null;
        const fileType = selectedFile?.type || row.file_type || null;

        return {
          ...(row.id ? { id: row.id } : {}),
          order_id: orderId,
          ack_id: row.ack_id || `${randomUUID()}_n`,
          location_id: locationId,
          osd_reason: row.osd_reason,
          osd_code: reasons.find((reason) => reason.description === row.osd_reason)?.code || "",
          notes: row.notes,
          qty: row.qty,
          units: row.units,
          file: selectedFile?.originFileObj || null,
          file_name: fileName,
          file_type: fileType,
          captured_at: getISTTimestamp(),
          status: row.status,
          authorized_by: row?.status === "CLOSED" ? row.authorized_by : "",
        };
      });

      const response = await SaveOsdDetails(orderId, payload);

      if (response?.success === false) {
        const errorMessage = response?.errors?.[0] || "Failed to save OSD details.";
        renderNotification(errorMessage);
        setErrorMsz(errorMessage);
        return;
      }

      message.success("Data saved successfully");
      setErrorMsz("");

      // ✅ Refresh/populate data after saving
      // await getOsdDetails(orderId);
      refreshDetails();
      onCancel(false);
      setLoading(false)

      // if (onSave) {
      //   onSave(response?.data || payload);
      // }

    } catch (error) {
      const errorMessage = error?.message || "Failed to save OSD details.";
      renderNotification(errorMessage);
      setErrorMsz(errorMessage);
    }
  };

  const handleCancel = async () => {
    setErrorMsz("");
    onCancel(false);
    if(trackApicall) {
    await refreshDetails();
    }
  }

  return (
    <Modal
      title="OSD Details"
      visible={visible}
      width={1400}
      destroyOnClose={false}
      onCancel={handleCancel}
      closable={false}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={loading}>
          Save
        </Button>,
      ]}
    >
{rows?.map((row, index) => (
  <div
    key={index}
    style={{
      marginBottom: 16,
      borderBottom: "1px solid #f0f0f0",
      paddingBottom: 16,
    }}
  >
    <Row gutter={16} type="flex" align="middle">
      {/* Left Content */}
      <Col span={22}>
        {/* First Line */}
        <Row gutter={16} type="flex" align="middle">
          {/* OSD Reason */}
          <Col style={{ flex: "3 0 200px" }}>
               <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: "20px",
                whiteSpace: "nowrap",
              }}
            >
              OSD Reason <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Select Reason"
              value={row.osd_reason}
              optionFilterProp="children"
              onChange={value => handleChange(index, "osd_reason", value)}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {reasons?.map(reason => (
                <Option key={reason.code} value={reason?.description}>
                  {reason?.description}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Qty */}
          <Col style={{ flex: "1 0 80px" }}>
             <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: "20px",
              }}
            >
              QTY
            </label>
            <Input
              min={1}
              style={{ width: "100%" }}
              type="number"
              value={row.qty}
              onChange={e => handleChange(index, "qty", e.target.value)}
            />
          </Col>

          {/* Units */}
          <Col style={{ flex: "1 0 100px" }}>
            {/* Units Input */}
             <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: "20px",
              }}
            >
              Units
            </label>
            <Input
              min={0}
              style={{ width: "100%" }}
              type="text"
              value={row.units}
              onChange={e => handleChange(index, "units", e.target.value)}
            />

          </Col>

          {/* Status */}
          <Col style={{ flex: "2 0 150px" }}>
            {/* Status Select */}
              <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: "20px",
              }}
            >
              Status
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Status"
              value={row.status}
              allowClear
              onChange={value => handleChange(index, "status", value)}
            >
              {OSD_STATUS_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Authorized By */}
          <Col style={{ flex: "2 0 180px" }}>
            {/* Authorized By Select */}
             <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: "20px",
              }}
            >
              Authorized By {row.status === "CLOSED" && <span style={{ color: "red" }}>*</span>}
            </label>
            <Select
              style={{ width: "100%" }}
              showSearch
              allowClear
              placeholder="Select User"
              value={row?.status === "CLOSED" ? row.authorized_by : ""}
              onChange={value => handleChange(index, "authorized_by", value)}
              disabled={row.status !== "CLOSED"}
            >
              {users.map(opt => (
                <Option key={opt.id} value={opt.full_name}>
                  {opt.full_name}
                </Option>
              ))}
            </Select>
          </Col>

          {/* File Upload */}
          <Col style={{ flex: "1 0 90px", minWidth: 0, height: "20px" }}>
            <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: "20px",
              }}
            >
              
            </label>
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              fileList={
                row.fileList?.length
                  ? row.fileList
                  : row.file_name
                  ? [
                      {
                        uid: row.id || row.file_name,
                        name: row.file_name,
                        status: "done",
                      },
                    ]
                  : []
              }
              onChange={info => handleUploadChange(index, info)}
            >
             <Button
                style={{
                  padding: "0 28px",
                  border: "none",
                  boxShadow: "none",
                  background: "transparent",
                }}
              >
                <Icon
                  type="cloud-upload"
                  style={{ fontSize: 30, cursor: "pointer" }}
                />
              </Button>
            </Upload>
          </Col>
        </Row>

        {/* Second Line - Notes */}
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <label
              style={{
                display: "block",
                fontWeight: 500,
                fontSize: 12,
                marginBottom: 4,
              }}
            >
              Notes
            </label>
              <TextArea
                rows={3}
                value={row.notes}
                onChange={e =>
                  handleChange(index, "notes", e.target.value)
                }
                placeholder="Enter Notes"
              />
          </Col>
        </Row>
      </Col>

      {/* Delete Button - Centered */}
      <Col
        span={2}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "start",
          height: "100%",
          position:"relative",
          bottom:"48px"
        }}
      >
        <Button
          type="danger"
          icon="delete"
          onClick={() => removeRow(index)}
        />
      </Col>
    </Row>
  </div>
))}

      <Button type="dashed" icon="plus" onClick={addRow}>
        Add More
      </Button>
      <div style={{ marginTop: 20 }}>
       {errorMsz?.length > 0 && (
          <div
            style={{
              border: "1px solid #ff6257",
              padding: "14px",
              background: "#FFF1F0",
              borderRadius: "4px",
              marginBottom: "12px",
            }}
          >
        {errorMsz.map((error, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: index !== errorMsz.length - 1 ? "8px" : 0,
            }}
          >
            <Icon
              type="close-circle"
              style={{
                color: "#d63941",
                fontSize: "16px",
                marginRight: "8px",
              }}
            />
            <span>{error}</span>
          </div>
        ))}
      </div>
    )}
      </div>
    </Modal>
  );
}

export default Modal_OSD;